/**
 * @fileoverview Text widget — single-line input.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Render single-line text input with placeholder, maxLength, pattern.
 *
 * Dependencies:
 * - React 18 (peer).
 *
 * Architecture:
 * - Stateless. Parent owns the value via `onChange`.
 *
 * @component TextField
 */
import { FieldWrapper } from './FieldWrapper';
import type { BaseWidgetProps } from './widgetTypes';

function safeRegex(p: string): RegExp | null {
  try { return new RegExp(p); } catch { return null; }
}

export function TextField(props: BaseWidgetProps<string | null | undefined>) {
  const { field, value, isNA, resolved, onChange, onToggleNA, errorMessage, requiredUnmet } = props;
  const extras = (field.extras ?? {}) as { placeholder?: string; maxLength?: number; pattern?: string };
  // Active validation — show aria-invalid when value violates pattern or
  // exceeds maxLength. Host can still override via `errorMessage`.
  const patternRe = extras.pattern ? safeRegex(extras.pattern) : null;
  const v = value ?? '';
  const exceedsMax = extras.maxLength != null && v.length > extras.maxLength;
  const patternMismatch = patternRe != null && v.length > 0 && !patternRe.test(v);
  const computedError = errorMessage
    ?? (exceedsMax ? `Max length ${extras.maxLength}` : undefined)
    ?? (patternMismatch ? 'Format does not match pattern' : undefined);

  return (
    <FieldWrapper
      fieldId={field.id}
      widgetType="text"
      label={field.label}
      helpText={field.helpText}
      required={field.required}
      resolved={resolved}
      isNA={isNA}
      naEnabled={field.naEnabled}
      onToggleNA={onToggleNA}
      errorMessage={computedError}
      requiredUnmet={requiredUnmet}
    >
      <input
        id={field.id}
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={extras.placeholder}
        maxLength={extras.maxLength}
        pattern={extras.pattern}
        disabled={!!resolved?.disabled}
        aria-required={!!(field.required || resolved?.required)}
        aria-invalid={!!computedError}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-sm outline-hidden focus:ring-2 focus:ring-blue-300 disabled:opacity-50"
      />
    </FieldWrapper>
  );
}
